import {
	AthleteMfaDeviceResponse,
	AthleteProfileResponse,
	ChangeEmail,
	ValidateChangeEmail,
	VerificationCodeRequest,
	VerificationRequest
} from '@my-account/data-access/interfaces';
import { Observable, catchError, throwError } from 'rxjs';

import { AthleteEngineDao } from '@my-account/data-access/dao';
import { Injectable } from '@angular/core';
import { MyAccountAnalyticsService } from '@shared/data-access/services';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class AthleteEngineService {

	constructor(private athleteEngineDao: AthleteEngineDao,
		private analyticsService: MyAccountAnalyticsService) {}

	/**
	 * @description
	 * Send reset link to mail
	 */
	public sendResetLink(): Observable<any> {
		return this.athleteEngineDao.sendResetLink().pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_SEND_RESET_LINK,
					'Failed to send reset link',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Updates the athlete's email address
	 */
	public updateEmail(changeEmail: ChangeEmail): Observable<any> {
		return this.athleteEngineDao.updateEmail(changeEmail).pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_UPDATE_EMAIL,
					'Failed to update email',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Updates the athlete's email address
	 */
	public validateTicketId(validateChangeEmail: ValidateChangeEmail): Observable<any> {
		return this.athleteEngineDao.validateTicketId(validateChangeEmail).pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_UPDATE_EMAIL,
					'Failed to validate ticketId',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Begins the email verification flow.
	 */
	public triggerVerificationFlow(verificationRequest: VerificationRequest): Observable<any> {
		return this.athleteEngineDao.triggerVerificationFlow(verificationRequest).pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to trigger verification flow',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Validates the verification code provided by athlete.
	 */
	public validateVerificationCode(verificationCodeRequest: VerificationCodeRequest): Observable<any> {
		return this.athleteEngineDao.validateEmailVerificationCode(verificationCodeRequest).pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to validate verification code',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Enrolls the athlete into MFA.
	 */
	public enableMFA(): Observable<any> {
		return this.athleteEngineDao.enableMFA().pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to enable MFA',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Fetches the devices athlete's mfa authenticators.
	 */
	public retrieveMFADevices(): Observable<AthleteMfaDeviceResponse[]> {
		return this.athleteEngineDao.retrieveMFADevices().pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to retrieve MFA devices',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Triggers the MFA enrollment flow for an athlete to add SMS as an MFA device
	 */
	public triggerMFAEnrollmentFlow(): Observable<any> {
		return this.athleteEngineDao.triggerMFAEnrollmentFlow().pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to trigger MFA enrollment flow',
					err.error
				);
				return throwError(err);
			})
		);
	}
	/**
	 * @description
	 * Retrieves athlete's profile info from Auth0
	 */
	public retrieveAthleteProfile(): Observable<AthleteProfileResponse> {
		return this.athleteEngineDao.retrieveAthleteProfile().pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to retrieve athlete profile',
					err.error
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description
	 * Deletes the athlete's registered MFA authenticator
	 */
	public deleteMFAAuthenticator(authenticatorId:string): Observable<any> {
		return this.athleteEngineDao.deleteMFAAuthenticator(authenticatorId).pipe(
			catchError((err:any) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
					'Failed to delete MFA authenticator',
					err.error
				);
				return throwError(err);
			})
		);
	}

		/**
	 * @description
	 * Disables MFA login flow for an athlete
	 */
		public disableMFA(code:string): Observable<any> {
			return this.athleteEngineDao.disableMFA(code).pipe(
				catchError((err:any) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_MFA_ENROLLMENT,
						'Failed to disable MFA',
						err.error
					);
					return throwError(err);
				})
			);
		}
}
